import { CommonModule } from "@angular/common";
import { Component, input, output } from "@angular/core";
import { AudioTranscriptionComponent } from "@components/audio-transcriber/audio-transcriber";
import { Indications } from "@components/indication/indications";
import { Answer } from "@domain/Answer";
import { AudioExercise as AudioExerciseEntity } from "@domain/Exercise";
import { AnswerFactory } from "@domain/factories/Answer.factory";

@Component({
  selector: "app-audio-exercise",
  imports: [CommonModule, AudioTranscriptionComponent, Indications],
  template: `
    <div data-testid="audio-exercise">
      <app-indications>Écris le bon mot dans la case pour compléter la phrase.</app-indications>

      <div class="exercise">
        <p class="exercise__answer-sentence" data-testid="exercise-answer-sentence">
          @for (sentencePart of exercise().getSentenceToCompleteParts(); track sentencePart) {
            @if (sentencePart === "[textfield]") {
              <div class="sentence-input">
                <app-audio-transcriber
                  [audioFile]="exercise().audioFileName"
                  [disabled]="answer()?.isSubmitted()"
                  [class]="getAudioTranscriberClass()"
                  name="audio-exercise-answer"
                  label="Entrer la bonne réponse"
                  [value]="answer()?.value ?? ''"
                  (changeValue)="handleAnswerChange($event)"
                  (playAudio)="playAudio.emit()"
                />
              </div>
            } @else {
              <span class="sentence-part">{{ sentencePart }}</span>
            }
          }
        </p>
      </div>
    </div>
  `,
  styles: `
    @use "responsive" as *;
    @use "typography" as *;

    .exercise {
      padding: 20px 0;
      margin-top: 20px;

      @include mobileScreenHeight("small") {
        padding: 0;
      }

      @include screenWidth("from-desktop") {
        margin-top: 40px;
      }
    }

    .exercise__sentence {
      line-height: 150%;
      padding-bottom: 24px;
      margin-bottom: 24px;
      border-bottom: 2px dashed #f5f3ef;
      @include body2();

      @include screenWidth("from-desktop") {
        padding-bottom: 28px;
        margin-bottom: 28px;
        @include body1();
      }
    }

    .exercise__answer-sentence {
      display: block;
      color: var(--text-primary, #152942);
      line-height: 46px;
      vertical-align: middle;
      @include body2();

      @include screenWidth("mobile-only") {
        line-height: 28px;
      }

      @include screenWidth("from-desktop") {
        @include body1();
      }
    }

    .sentence-input {
      display: inline;
      line-height: 56px;
      vertical-align: middle;
    }

    .sentence-part,
    .sentence-input {
      &:not(:last-child) {
        margin-right: 8px;

        @include screenWidth("to-mobile-small") {
          margin-right: 0px;
        }

        @include screenWidth("from-desktop") {
          margin-right: 12px;
        }
      }
    }
  `,
})
export class AudioExercise {
  exercise = input.required<AudioExerciseEntity>();
  answer = input.required<Answer | undefined>();

  updateAnswer = output<Answer | undefined>();
  playAudio = output<void>();

  protected handleAnswerChange(newValue: string) {
    if (newValue === "") {
      this.updateAnswer.emit(undefined);
      return;
    }

    this.updateAnswer.emit(AnswerFactory.createNewAnswer(newValue));
  }

  protected getAudioTranscriberClass() {
    const answer = this.answer();

    if (!answer) return "";

    if (answer.isCorrect()) {
      return "is-correct";
    }

    if (answer.isIncorrect()) {
      return "is-incorrect";
    }

    return "";
  }
}
